import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useData } from "../../contexts/DataContext";

import axios from "axios";
import { ArrowLeft, Edit2 } from "lucide-react";
import { API_CONFIG } from "../../api/config";
import Button from "../../ui/Buttons";
import { studentApi, calendarApi } from "../../api/services";
import StudentFeeDashboard from "../Fee/components/StudentFeeDashboard";
import { feeApi } from "../../api/services/feeApi";
import Loader from "../../ui/Loader";

const monthsData = [
  { month: 1, label: "January" },
  { month: 2, label: "February" },
  { month: 3, label: "March" },
  { month: 4, label: "April" },
  { month: 5, label: "May" },
  { month: 6, label: "June" },
  { month: 7, label: "July" },
  { month: 8, label: "August" },
  { month: 9, label: "September" },
  { month: 10, label: "October" },
  { month: 11, label: "November" },
  { month: 12, label: "December" },
];

const COLORS = {
  present: "#25C561",
  absent: "#ED4441",
  holiday: "#FCD34D",
  nodate: "#E8E7EC",
};

function MonthlyAttendanceList({ enrollmentId, studentName, academicYear, studentRegistrationId }) {
  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(today.getMonth() + 1);
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [monthAttendance, setMonthAttendance] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState("blocks");

  useEffect(() => {
    const mm = String(currentMonth).padStart(2, "0");
    const first = `${currentYear}-${mm}-01`;
    
    const lastDayOfMonth = new Date(currentYear, currentMonth, 0).getDate();
    
    const currentDate = new Date();
    let toDateStr;
    
    if (currentYear === currentDate.getFullYear() && currentMonth === (currentDate.getMonth() + 1)) {
       const todayDay = String(currentDate.getDate()).padStart(2, "0");
       toDateStr = `${currentYear}-${mm}-${todayDay}`;
    } else {
       toDateStr = `${currentYear}-${mm}-${String(lastDayOfMonth).padStart(2, "0")}`;
    }

    setFromDate(first);
    setToDate(toDateStr);
  }, [currentMonth, currentYear]);

  const fetchMonthlyAttendance = useCallback(async () => {
    try {
      setLoading(true);

      const url = `${API_CONFIG.BASE_URL}/attendance/month/enrollment?enrollmentId=${enrollmentId}&academicYear=${academicYear}&from=${fromDate}&to=${toDate}`;

      const [attendanceRes, holidaysRes] = await Promise.all([
        axios.get(url, { headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` } }),
        calendarApi.getEventsByRange(fromDate, toDate)
      ]);

      // 🔥 FIX TIMEZONE ISSUE HERE
      const normalizedData = (attendanceRes.data?.data || []).map((item) => ({
        ...item,
        date: new Date(item.date).toISOString().split("T")[0], // Convert to pure date
      }));

      // Normalize holidays
      let normalizedHolidays = [];
      if (holidaysRes?.success) {
         const events = Array.isArray(holidaysRes.data) ? holidaysRes.data : 
                       (holidaysRes.data?.data ? holidaysRes.data.data : []);
         
         normalizedHolidays = events
            .filter(e => e.eventType === 'holiday' || e.type === 'holiday' || e.isHoliday)
            .map(e => ({
                date: new Date(e.startDate || e.date).toISOString().split("T")[0],
                title: e.title || e.eventName || 'Holiday'
            }));
      }

      setMonthAttendance(normalizedData);
      setHolidays(normalizedHolidays);
    } catch (e) {
      setMonthAttendance([]);
      setHolidays([]);
    } finally {
      setLoading(false);
    }
  }, [enrollmentId, academicYear, fromDate, toDate]);

  useEffect(() => {
    if (!fromDate || !toDate || !enrollmentId) return;
    fetchMonthlyAttendance();
  }, [fetchMonthlyAttendance, fromDate, toDate, enrollmentId]);

  const getAttendanceForDate = (date) => {
    if (!date) return null;
    // Use local date components instead of toISOString to avoid timezone shifts
    const offset = date.getTimezoneOffset();
    const dateStr = new Date(date.getTime() - (offset * 60 * 1000)).toISOString().split('T')[0];
    
    // Check for explicit attendance record first
    const record = monthAttendance.find((r) => r.date === dateStr);
    if (record) return record;

    // Fallback to calendar holiday
    const holiday = holidays.find(h => h.date === dateStr);
    if (holiday) {
        return { date: dateStr, status: 'Holiday', isHoliday: true, description: holiday.title };
    }

    return null;
  };

  const getAttendanceColor = (attendance) => {
    if (!attendance) return COLORS.nodate;
    if (attendance.isHoliday || attendance.status === 'Holiday') return COLORS.holiday;
    if (attendance.status === "present") return COLORS.present;
    if (attendance.status === "absent") return COLORS.absent;
    return COLORS.nodate;
  };

  const generateWeeklyGrid = (year, month) => {
    const lastOfMonth = new Date(year, month, 0);
    const weeks = [];
    let week = new Array(7).fill(null);
    for (let d = 1; d <= lastOfMonth.getDate(); d++) {
      const date = new Date(year, month - 1, d);
      const dayIndex = date.getDay();
      week[dayIndex] = date;
      if (dayIndex === 6 || d === lastOfMonth.getDate()) {
        weeks.push(week);
        week = new Array(7).fill(null);
      }
    }
    return weeks;
  };

  const getMonthsInRange = () => {
    if (!fromDate || !toDate)
      return [{ month: currentMonth, year: currentYear }];
    const f = new Date(fromDate + "T00:00:00");
    const t = new Date(toDate + "T00:00:00");
    const months = [];
    const cur = new Date(f.getFullYear(), f.getMonth(), 1);
    while (cur <= t) {
      months.push({ month: cur.getMonth() + 1, year: cur.getFullYear() });
      cur.setMonth(cur.getMonth() + 1);
    }
    return months;
  };

  const handleMonthChange = (offset) => {
    const newDate = new Date(currentYear, currentMonth - 1 + offset, 1);
    setCurrentMonth(newDate.getMonth() + 1);
    setCurrentYear(newDate.getFullYear());
  };

  const goToPrevMonth = () => handleMonthChange(-1);
  const goToNextMonth = () => handleMonthChange(1);

  // const weeksMemo = useMemo(() => {
  //   return generateWeeklyGrid(currentYear, currentMonth);
  // }, [currentYear, currentMonth, monthAttendance]);

  const months = getMonthsInRange();

  const summary = (() => {
    const currentMonthData = monthAttendance.filter((d) => {
      const date = new Date(d.date);
      return (
        date.getMonth() + 1 === currentMonth &&
        date.getFullYear() === currentYear
      );
    });

    const holidays = currentMonthData.filter((d) => d.isHoliday).length;
    const presentDays = currentMonthData.filter(
      (d) => d.status === "present" && !d.isHoliday
    ).length;
    const absentDays = currentMonthData.filter(
      (d) => d.status === "absent" && !d.isHoliday
    ).length;
    
    // Total working days (excluding holidays)
    const totalDays = presentDays + absentDays;
    
    const attendancePercent = totalDays
      ? Math.round((presentDays / totalDays) * 100)
      : 0;

    return { totalDays, presentDays, absentDays, holidays, attendancePercent };
  })();

  return (
    <div className="">
      <div className="space-y-2 ">
        <div className="p-4 bg-white border border-gray-100 rounded-lg shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center space-x-2">
              {" "}
              <div className="flex items-center justify-center w-12 h-12 text-lg font-semibold text-white rounded-full bg-gradient-to-br from-blue-400 to-blue-600">
                {studentName
                  .split(" ")
                  .map((n) => n[0])
                  .slice(0, 2)
                  .join("")}
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {studentName}
                </h2>
                <p className="text-xs text-gray-600">
                  Student ID: {studentRegistrationId || enrollmentId}
                </p>
              </div>
            </div>

            <div className="flex items-end gap-2">
              {/* From */}
              <div className="flex flex-col">
                <label className="text-[10px] text-gray-600">From</label>
                <input
                  value={fromDate}
                  onChange={(e) => {
                    setFromDate(e.target.value);
                    const d = new Date(e.target.value + "T00:00:00");
                    if (!isNaN(d)) {
                      setCurrentMonth(d.getMonth() + 1);
                      setCurrentYear(d.getFullYear());
                    }
                  }}
                  type="date"
                  className="mt-0.5 border border-gray-300 rounded-md px-2 py-1 text-xs"
                />
              </div>

              {/* To */}
              <div className="flex flex-col">
                <label className="text-[10px] text-gray-600">To</label>
                <input
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  type="date"
                  className="mt-0.5 border border-gray-300 rounded-md px-2 py-1 text-xs"
                />
              </div>

              {/* Buttons */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    if (fromDate && toDate) fetchMonthlyAttendance();
                  }}
                  className="px-3 py-1.5 bg-blue-600 text-white rounded-md text-xs"
                >
                  Apply
                </button>

                <button
                  onClick={() => {
                    const f = new Date();
                    setCurrentMonth(f.getMonth() + 1);
                    setCurrentYear(f.getFullYear());
                  }}
                  className="px-3 py-1.5 bg-white border border-gray-300 rounded-md text-xs"
                >
                  Reset
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          <div className="space-y-4">
            <div className="p-4 bg-white border border-gray-100 rounded-lg shadow-sm">
              <h3 className="mb-2 text-sm font-medium text-gray-900">
                Academic Year ({academicYear})
              </h3>
              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex justify-between">
                  <span>Total Working Days</span>
                  <span className="font-semibold text-gray-900">
                    {summary.totalDays || "-"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Present Days</span>
                  <span
                    className="font-semibold"
                    style={{ color: COLORS.present }}
                  >
                    {summary.presentDays || "-"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Absent Days</span>
                  <span
                    className="font-semibold"
                    style={{ color: COLORS.absent }}
                  >
                    {summary.absentDays || "-"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Holidays</span>
                  <span
                    className="font-semibold"
                    style={{ color: COLORS.holiday }}
                  >
                    {summary.holidays || "-"}
                  </span>
                </div>
                <div className="pt-3 border-t border-gray-100">
                  <div className="flex items-center justify-between mb-2 text-xs text-gray-600">
                    <span>Attendance</span>
                    <span className="font-semibold text-gray-900">
                      {summary.attendancePercent || 0}%
                    </span>
                  </div>
                  <div className="w-full h-2 bg-gray-200 rounded-full">
                    <div
                      className="h-2 rounded-full"
                      style={{
                        width: `${summary.attendancePercent}%`,
                        backgroundColor: COLORS.present,
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="p-4 bg-white border border-gray-100 rounded-lg shadow-sm">
              <h3 className="mb-2 text-sm font-medium text-gray-900">
                Current Month (
                {monthsData.find((m) => m.month === currentMonth)?.label})
              </h3>
              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex justify-between">
                  <span>Total Working Days</span>
                  <span className="font-semibold text-gray-900">
                    {summary.totalDays || "-"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Present Days</span>
                  <span
                    className="font-semibold"
                    style={{ color: COLORS.present }}
                  >
                    {summary.presentDays || "-"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Absent Days</span>
                  <span
                    className="font-semibold"
                    style={{ color: COLORS.absent }}
                  >
                    {summary.absentDays || "-"}
                  </span>
                </div>
                <div className="flex justify-between">
                    <span>Holidays</span>
                    <span
                        className="font-semibold"
                        style={{ color: COLORS.holiday }}
                    >
                        {summary.holidays || "-"}
                    </span>
                </div>
                <div className="pt-3 border-t border-gray-100">
                  <div className="flex items-center justify-between mb-2 text-xs text-gray-600">
                    <span>Attendance</span>
                    <span className="font-semibold text-gray-900">
                      {summary.attendancePercent || 0}%
                    </span>
                  </div>
                  <div className="w-full h-2 bg-gray-200 rounded-full">
                    <div
                      className="h-2 rounded-full"
                      style={{
                        width: `${summary.attendancePercent}%`,
                        backgroundColor: COLORS.present,
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="p-4 bg-white border border-gray-100 rounded-lg shadow-sm lg:col-span-2">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-900">Attendance</h3>
              <div className="flex items-center gap-3 ml-auto">
                <button
                  onClick={goToPrevMonth}
                  className="px-3 py-1 text-xs font-medium bg-white border border-gray-200 rounded-md"
                >
                  Prev
                </button>
                <div className="px-3 py-2 text-xs text-gray-700 bg-white border border-gray-100 rounded">
                  {monthsData.find((m) => m.month === currentMonth)?.label}{" "}
                  {currentYear}
                </div>
                <button
                  onClick={goToNextMonth}
                  className="px-3 py-1 text-xs font-medium bg-white border border-gray-200 rounded-md"
                >
                  Next
                </button>
              </div>
              <div className="flex items-center gap-2 ml-2">
                <button
                  onClick={() => setViewMode("blocks")}
                  className={`px-3 py-1 rounded-md text-xs font-medium ${
                    viewMode === "blocks"
                      ? "bg-gray-900 text-white"
                      : "bg-white border border-gray-200"
                  }`}
                >
                  Blocks View
                </button>
                <button
                  onClick={() => setViewMode("list")}
                  className={`px-3 py-1 rounded-md text-xs font-medium ${
                    viewMode === "list"
                      ? "bg-gray-900 text-white"
                      : "bg-white border border-gray-200"
                  }`}
                >
                  List View
                </button>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 mb-4">
              <div className="flex items-center gap-2">
                <div
                  style={{ backgroundColor: COLORS.present }}
                  className="w-3 h-3 rounded"
                />{" "}
                <span className="text-xs text-gray-600">Present</span>
              </div>
              <div className="flex items-center gap-2">
                <div
                  style={{ backgroundColor: COLORS.absent }}
                  className="w-3 h-3 rounded"
                />{" "}
                <span className="text-xs text-gray-600">Absent</span>
              </div>
              <div className="flex items-center gap-2">
                <div
                  style={{ backgroundColor: COLORS.holiday }}
                  className="w-3 h-3 rounded"
                />{" "}
                <span className="text-xs text-gray-600">Holiday</span>
              </div>
              <div className="flex items-center gap-2">
                <div
                  style={{ backgroundColor: COLORS.nodate }}
                  className="w-3 h-3 border border-gray-200 rounded"
                />{" "}
                <span className="text-xs text-gray-600">Blank</span>
              </div>
            </div>

            {loading ? (
              <Loader />
            ) : (
              <>
                {viewMode === "blocks" ? (
                  <div className="flex flex-wrap gap-4 max-h-[45vh] overflow-y-auto">
                    {months.map((m, idx) => {
                      const mg = generateWeeklyGrid(m.year, m.month);
                      return (
                        <div
                          key={`${m.month}-${m.year}-${idx}`}
                          className="w-full p-2 sm:w-1/2 md:w-1/4"
                        >
                          <div className="p-3 bg-white border border-gray-100 rounded shadow-sm">
                            <div className="text-[13px] font-medium text-gray-800 mb-2">
                              {
                                monthsData.find((x) => x.month === m.month)
                                  ?.label
                              }{" "}
                              {m.year}
                            </div>
                            <div className="flex gap-2">
                              {mg.map((week, wi) => (
                                <div key={wi} className="flex flex-col gap-2">
                                  {week.map((date, di) => {
                                    const attendance = date
                                      ? getAttendanceForDate(date)
                                      : null;
                                    const color =
                                      getAttendanceColor(attendance);
                                    const blank = !date;
                                    
                                    const dateStr = date ? new Date(date).toLocaleDateString('en-IN', {
                                      day: 'numeric',
                                      month: 'short',
                                      year: 'numeric'
                                    }) : '';
                                    const statusText = attendance ? attendance.status : 'No record';
                                    
                                    return (
                                      <div
                                        key={di}
                                        className={`w-4 h-4 rounded-sm ${
                                          blank
                                            ? "bg-white border border-dashed border-gray-300"
                                            : ""
                                        }`}
                                        style={
                                          !blank
                                            ? { backgroundColor: color }
                                            : {}
                                        }
                                        title={!blank ? `${dateStr} - ${statusText}` : ''}
                                      />
                                    );
                                  })}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="space-y-4 max-h-[45vh] overflow-y-auto pr-2">
                    {months.map((m, idx) => {
                      const mg = generateWeeklyGrid(m.year, m.month);
                      return (
                        <div
                          key={`${m.month}-${m.year}-${idx}`}
                          className="p-3 bg-white border border-gray-100 rounded shadow-sm"
                        >
                          <div className="text-[13px] font-medium text-gray-800 mb-2">
                            {monthsData.find((x) => x.month === m.month)?.label}{" "}
                            {m.year}
                          </div>
                          <div className="grid grid-cols-7 gap-2 text-[11px] font-semibold text-gray-500 mb-2">
                            {[
                              "Sun",
                              "Mon",
                              "Tue",
                              "Wed",
                              "Thu",
                              "Fri",
                              "Sat",
                            ].map((d) => (
                              <div key={d} className="text-center">
                                {d}
                              </div>
                            ))}
                          </div>
                          <div className="space-y-2">
                            {mg.map((week, wi) => (
                              <div key={wi} className="flex gap-2">
                                {week.map((date, di) => {
                                  const attendance = date
                                    ? getAttendanceForDate(date)
                                    : null;
                                  const color = getAttendanceColor(attendance);
                                  const blank = !date;
                                  
                                  const dateStr = date ? new Date(date).toLocaleDateString('en-IN', {
                                    day: 'numeric',
                                    month: 'short',
                                    year: 'numeric'
                                  }) : '';
                                  const statusText = attendance ? attendance.status : 'No record';
                                  
                                  return (
                                    <div
                                      key={di}
                                      className={`flex-1 h-8 rounded-md ${
                                        blank
                                          ? "bg-white border border-dashed border-gray-300"
                                          : ""
                                      }`}
                                      style={
                                        !blank ? { backgroundColor: color } : {}
                                      }
                                      title={!blank ? `${dateStr} - ${statusText}` : ''}
                                    />
                                  );
                                })}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const tabs = [
  { key: "info", label: "Info" },
  { key: "attendance", label: "Attendance" },
  { key: "fees", label: "Fees" },
  { key: "history", label: "Enrollment History" },
];

const EnrollmentHistory = ({ studentId, classes }) => {
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!studentId) {
      setError("Student ID is required");
      setLoading(false);
      return;
    }

    const fetchEnrollmentHistory = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await axios.get(
          `${API_CONFIG.BASE_URL}/enrollment/studentHistory?student=${studentId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            },
          }
        );

        if (response.data.success) {
          setEnrollments(response.data.data);
        } else {
          setError(
            response.data.message || "Failed to fetch enrollment history"
          );
        }
      } catch (err) {
        setError(err.response?.data?.message || "Failed to connect to server");
      } finally {
        setLoading(false);
      }
    };

    fetchEnrollmentHistory();
  }, [studentId]);

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  const getStatusBadge = (status) => {
    return status === "active"
      ? "bg-green-100 text-green-800"
      : "bg-gray-100 text-gray-700";
  };

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return (
      <div className="max-w-md p-6 py-12 mx-auto text-sm font-medium text-center text-red-600 rounded-lg bg-red-50">
        {error}
      </div>
    );
  }

  return (
    <div className="">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Enrollment History</h2>
        <p className="mt-1 text-sm text-gray-600">
          Academic enrollment records for the student
        </p>
      </div>

      <div className="overflow-hidden bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs tracking-wider text-gray-600 uppercase bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left">Academic Year</th>
                <th className="px-6 py-3 text-left">Joined On</th>
                <th className="px-6 py-3 text-left">Unit / Branch</th>
                <th className="px-6 py-3 text-left">Section</th>
                <th className="px-6 py-3 text-left">Status</th>
                <th className="px-6 py-3 text-left">Enrolled On</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {enrollments.length === 0 ? (
                <tr>
                  <td colSpan="6" className="py-12 text-center text-gray-500">
                    No enrollment records found
                  </td>
                </tr>
              ) : (
                enrollments.map((enrollment) => (
                  <tr
                    key={enrollment._id}
                    className="transition hover:bg-gray-50"
                  >
                    <td className="px-6 py-4 font-medium text-gray-900">
                      {enrollment.academicYear || "-"}
                    </td>
                    <td className="px-6 py-4 text-gray-700">
                      {enrollment.timeline?.joinedOn
                        ? formatDate(enrollment.timeline.joinedOn)
                        : "-"}
                    </td>
                    <td className="px-6 py-4 text-gray-700">
                      {(() => {
                        if (enrollment.unitId && typeof enrollment.unitId === 'object') {
                          return enrollment.unitId.unitName || enrollment.unitId.name || "Main Campus";
                        }
                        const unit = classes?.find(c => c._id === enrollment.unitId);
                        return unit ? unit.unitName : (enrollment.unitId || "Main Campus");
                      })()}
                    </td>
                    <td className="px-6 py-4 text-gray-600">
                      {(() => {
                        if (!enrollment.sectionId) return "—";
                        if (typeof enrollment.sectionId === 'object') {
                          return enrollment.sectionId.sectionName || enrollment.sectionId.name || "—";
                        }
                        // If it's just an ID string, try to find it in the unit's sections
                        const unit = classes?.find(c => c._id === enrollment.unitId || c._id === enrollment.unitId?._id);
                        const section = unit?.sections?.find(s => s._id === enrollment.sectionId);
                        return section ? (section.sectionName || section.name) : enrollment.sectionId;
                      })()}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${getStatusBadge(
                          enrollment.status
                        )}`}
                      >
                        {enrollment.status?.charAt(0).toUpperCase() +
                          enrollment.status?.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-xs text-gray-500">
                      {formatDate(enrollment.createdAt)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {enrollments.length > 0 && (
        <div className="p-4 mt-6 text-sm border border-indigo-200 rounded-lg bg-indigo-50">
          <p className="font-medium text-indigo-800">
            Currently Active:{" "}
            <span className="font-bold">
              {enrollments.find((e) => e.status === "active")?.academicYear ||
                "None"}
            </span>
          </p>
        </div>
      )}
    </div>
  );
};
function StudentInfo({ student, parents }) {
  const formatDate = (date) => {
    if (!date) return "—";
    try {
      return new Date(date).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return date;
    }
  };

  return (
    <div className="p-1">
      <div className="w-full overflow-y-auto bg-white max-h-[calc(80vh-120px)]">
        <div className="px-5 py-6 text-black">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between ">
            <div className="flex items-center gap-4">
              <div className="flex items-center justify-center w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm">
                <svg
                  className="w-8 h-8"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-semibold">
                  {student.firstName} {student.lastName}
                </h1>
                <p className="mt-1 text-xs opacity-90">
                  ID: {student.studentRegistrationId}
                </p>
              </div>
            </div>
            <span
              className={`self-start sm:self-center px-3 py-1 rounded-full text-xs font-medium ${
                student.status?.toLowerCase() === "active"
                  ? "bg-green-100 text-green-800"
                  : "bg-gray-100 text-gray-800"
              }`}
            >
              {student.status || "Active"}
            </span>
          </div>
        </div>

        <div className="p-4">
          <div className="space-y-4">
            {/* Personal Information */}
            <section>
              <h2 className="pb-2 mb-4 text-base font-medium text-gray-800 border-b border-gray-200">
                Personal Information
              </h2>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                <InfoRow
                  label="Email"
                  value={student.email}
                  icon="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
                <InfoRow
                  label="Phone"
                  value={student.phoneNumber || "—"}
                  icon="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                />
                <InfoRow
                  label="Gender"
                  value={student.gender || "—"}
                  icon="M12 4.354a6 6 0 110 11.292A8 8 0 0112 4.354zM16 8a4 4 0 11-8 0 4 4 0 018 0z"
                />
                <InfoRow
                  label="Date of Birth"
                  value={formatDate(student.dateOfBirth)}
                  icon="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
                <InfoRow
                  label="Admission Date"
                  value={formatDate(student.admissionDate)}
                  icon="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </div>
            </section>

            {/* Address */}
            <section>
              <h2 className="pb-2 mb-4 text-base font-medium text-gray-800 border-b border-gray-200">
                Address
              </h2>
              <div className="flex items-start gap-4 p-4 rounded-lg bg-gray-50">
                <svg
                  className="w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                <p className="text-sm leading-relaxed text-gray-700">
                  {student.address.street}, {student.address.city},{" "}
                  {student.address.state}
                  <br />
                  {student.address.country} – {student.address.postalCode}
                </p>
              </div>
            </section>

            {/* Enrollment */}
            <section>
              <h2 className="pb-2 mb-4 text-base font-medium text-gray-800 border-b border-gray-200">
                Enrollment Details
              </h2>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                <InfoRow
                  label="Class"
                  value={student.enrollment?.unitId?.unitName || student.enrollment?.unitName || "—"}
                  icon="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h-4m-6 0H5"
                />
                <InfoRow
                  label="Section"
                  value={student.enrollment?.sectionId?.sectionName || student.enrollment?.sectionId?.name || "—"}
                  icon="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h-4m-6 0H5"
                />
                <InfoRow
                  label="Academic Year"
                  value={student.enrollment?.academicYear || "—"}
                  icon="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5$"
                />
                <InfoRow
                  label="Status"
                  value={student.enrollment?.status || "—"}
                  icon="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </div>
            </section>

            {/* Parent */}
            <section>
              <h2 className="pb-2 mb-4 text-base font-medium text-gray-800 border-b border-gray-200">
                Parent / Guardian
              </h2>
              <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                <div className="flex items-center gap-4">
                  <div className="flex items-center justify-center bg-gray-200 rounded-full w-11 h-11">
                    <svg
                      className="w-6 h-6 text-gray-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                  </div>
                  <div>
                    {(() => {
                      const parent = typeof student.parentId === 'object' 
                        ? student.parentId 
                        : parents?.find(p => p._id === student.parentId);
                      
                      if (!parent) return <p className="text-sm text-gray-500 italic">No parent info provided</p>;

                      return (
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-bold text-gray-900 leading-none">{parent.name}</p>
                            <span className="text-[10px] uppercase px-1.5 py-0.5 bg-indigo-50 text-indigo-700 rounded-md font-bold tracking-wider">{parent.relation}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <p className="text-xs text-slate-600 flex items-center gap-1.5 font-medium">
                              <span className="text-slate-400">Phone:</span> {parent.phoneNumber || parent.phone}
                            </p>
                            {parent.hasWhatsapp && (
                              <span className="inline-flex items-center gap-1 text-[10px] text-emerald-600 font-bold bg-emerald-50 px-1.5 py-0.5 rounded">
                                <span className="w-1 h-1 bg-emerald-500 rounded-full"></span>
                                WhatsApp
                              </span>
                            )}
                            {parent.email && (
                              <p className="text-xs text-slate-600 flex items-center gap-1.5 font-medium">
                                <span className="text-slate-400">Email:</span> {parent.email}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ label, value, icon }) {
  return (
    <div className="flex items-start gap-3">
      <div className="flex items-center justify-center flex-shrink-0 bg-gray-100 rounded-lg w-9 h-9">
        <svg
          className="w-4.5 h-4.5 text-gray-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d={icon}
          />
        </svg>
      </div>
      <div>
        <p className="text-xs font-medium tracking-wider text-gray-500 uppercase">
          {label}
        </p>
        <p className="text-sm font-medium text-gray-900 mt-0.5">{value}</p>
      </div>
    </div>
  );
}

const StudentDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isTeacher, user } = useAuth();
  const { classes: apiClasses, parents: apiParents, currentAcademicYear } = useData(); // Removed apiStudents
  const [activeTab, setActiveTab] = useState("info");
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);

  const teacherUnitIds = useMemo(() => user?.assignedUnitIds || [], [user]);

  useEffect(() => {
    const fetchStudent = async () => {
      setLoading(true);
      setAccessDenied(false);
      try {
        const response = await studentApi.getStudentById(id);
        if (response.success) {
          setStudent(response.data);
        } else {
          if (response.status === 403) setAccessDenied(true);
          setStudent(null);
        }
      } catch (error) {
        if (error.response?.status === 403) {
          setAccessDenied(true);
        }
        setStudent(null);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchStudent();
    }
  }, [id]);

  if (loading) {
    return <Loader />;
  }

  if (!student) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center">
        <div className="text-center">
          <h2 className="mb-2 text-xl font-semibold text-gray-900">
            Student not found
          </h2>
          <button onClick={() => navigate("/students")} className="btn-primary">
            Back to Students
          </button>
        </div>
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center">
        <div className="text-center">
          <h2 className="mb-2 text-xl font-semibold text-gray-900">
            Access restricted
          </h2>
          <p className="text-gray-600">
            You do not have permission to view this student's details.
          </p>
          <button onClick={() => navigate("/students")} className="btn-primary mt-4">
            Back to Students
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{student.firstName} {student.lastName}</h1>
          <p className="text-gray-600">{student.unitName} • {student.registrationId}</p>
        </div>

      </div> */}

      <div className="">
        <div className="flex items-center justify-between border-b border-gray-200">
          <div>
            {" "}
            {tabs
              .filter((t) => !(isTeacher && t.key === "fees"))
              .map((t) => (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                className={`px-4 py-2 text-sm font-medium ${
                  activeTab === t.key
                    ? "text-primary-600 border-b-2 border-primary-600"
                    : "text-gray-600"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="flex justify-end space-x-2 ">
            <Button
              onClick={() => navigate("/students")}
              size="sm"
              className="flex items-center space-x-2"
            >
              <ArrowLeft className="w-3 h-4 font-bold" />
              <span>Back</span>
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={() => navigate(`/students/${id}/edit`)}
              className="flex items-center space-x-2"
            >
              <Edit2 className="w-3 h-4 font-bold" />
              <span>Update</span>
            </Button>
          </div>
        </div>
        <div className="p-4">
          {activeTab === "info" && <StudentInfo student={student} parents={apiParents} />}
          {activeTab === "attendance" && (
            <div className="text-gray-700">
              <MonthlyAttendanceList
                enrollmentId={student.enrollment?._id}
                studentName={`${student.firstName} ${student.lastName}`}
                academicYear={student.enrollment?.academicYear || currentAcademicYear}
                studentRegistrationId={student.studentRegistrationId}
              />
            </div>
          )}
          {activeTab === "fees" && (
            <StudentFeeDashboard
              enrollmentId={student?.enrollment?._id}
              academicYear={student?.enrollment?.academicYear}
              feeApi={feeApi}
              formatCurrency={(amount) => `₹ ${(amount || 0).toLocaleString('en-IN')}`}
            />
          )}
          {activeTab === "history" && (
            <EnrollmentHistory studentId={student._id} classes={apiClasses} />
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentDetails;
